/**
 * Base Chunk Uploader
 *
 * Abstract base class for chunked file uploads with:
 * - File splitting and MD5 calculation
 * - Parallel chunk upload with retry logic
 * - blkWritten verification and retry
 * - Pause/Resume/Cancel support with AbortController
 */

import SparkMD5 from 'spark-md5'

import { DEFAULT_UPLOAD_CONCURRENCY } from '@/consts/upload'
import { ConcurrentPool, TaskResult } from '@/utils/concurrent-pool'

import {
  BaseChunkUploaderOptions,
  calculateRetryDelay,
  ChunkUploadFn,
  ChunkUploadResponse,
  DEFAULT_CHUNK_SIZE,
  DEFAULT_RETRY_DELAYS,
  DEFAULT_RETRY_TIMES,
  UploadPausedError,
} from './types'

function getBlobSlice() {
  return (
    File.prototype.slice || (File.prototype as any).mozSlice || (File.prototype as any).webkitSlice
  )
}

export abstract class BaseChunkUploader {
  protected chunkSize: number
  protected concurrency: number
  protected retryTimes: number
  protected retryDelays: number[]

  protected canceled: boolean = false
  protected paused: boolean = false
  protected activeAbortControllers: Set<AbortController> = new Set()

  constructor(options: BaseChunkUploaderOptions = {}) {
    this.chunkSize = options.chunkSize ?? DEFAULT_CHUNK_SIZE
    this.concurrency = options.concurrency ?? DEFAULT_UPLOAD_CONCURRENCY
    this.retryTimes = options.retryTimes ?? DEFAULT_RETRY_TIMES
    this.retryDelays = options.retryDelays ?? DEFAULT_RETRY_DELAYS
  }

  // === Public Control Methods ===

  /**
   * Pause the upload immediately by aborting all active requests
   */
  public pause(): void {
    this.paused = true
    // Abort all active requests for immediate pause
    this.activeAbortControllers.forEach((controller) => controller.abort())
    this.activeAbortControllers.clear()
  }

  /**
   * Resume the upload (must call specific resume method to continue)
   */
  public resume(): void {
    this.paused = false
  }

  /**
   * Cancel the upload
   */
  public cancel(): void {
    this.canceled = true
    // Also abort all active requests
    this.activeAbortControllers.forEach((controller) => controller.abort())
    this.activeAbortControllers.clear()
  }

  /**
   * Check if upload is paused
   */
  public isPaused(): boolean {
    return this.paused
  }

  /**
   * Check if upload is canceled
   */
  public isCanceled(): boolean {
    return this.canceled
  }

  // === Chunk Processing ===

  /**
   * Split file into chunks and calculate MD5
   * @param file File to process
   * @returns MD5 hash and chunk list
   */
  public async getChunkListAndFileMd5(file: File): Promise<{ md5: string; chunkList: Blob[] }> {
    return new Promise((resolve, reject) => {
      let currentChunk = 0
      const chunkSize = this.chunkSize
      const chunks = Math.ceil(file.size / chunkSize)
      const spark = new SparkMD5.ArrayBuffer()
      const fileReader = new FileReader()
      const blobSlice = getBlobSlice()
      const chunkList: Blob[] = []

      fileReader.onload = function (e) {
        if (e?.target?.result instanceof ArrayBuffer) {
          spark.append(e.target.result)
        }
        currentChunk++

        if (currentChunk < chunks) {
          loadNextChunk()
        } else {
          const computedHash = spark.end()
          resolve({ md5: computedHash, chunkList })
        }
      }

      fileReader.onerror = function (e) {
        console.warn('read file error', e)
        reject(e)
      }

      function loadNextChunk() {
        const start = currentChunk * chunkSize
        const end = start + chunkSize >= file.size ? file.size : start + chunkSize
        const chunk = blobSlice.call(file, start, end)
        chunkList.push(chunk)
        fileReader.readAsArrayBuffer(chunk)
      }

      loadNextChunk()
    })
  }

  // === Protected Utility Methods ===

  /**
   * Check if upload should abort (canceled or paused)
   */
  protected shouldAbort(): boolean {
    return this.canceled || this.paused
  }

  /**
   * Delay execution for specified milliseconds
   */
  protected delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Upload a single chunk with infinite retry logic and abort support
   * Retries indefinitely with fixed delay until success, pause, or cancel
   */
  protected async uploadSingleChunkWithRetry<TContext, TResponse extends ChunkUploadResponse>(
    chunk: Blob,
    index: number,
    context: TContext,
    uploadFn: ChunkUploadFn<TContext, TResponse>,
  ): Promise<TResponse> {
    let attempt = 0

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Check pause/cancel before each attempt
      if (this.paused) throw new UploadPausedError()
      if (this.canceled) throw new Error('canceled')

      // Create AbortController for this request
      const abortController = new AbortController()
      this.activeAbortControllers.add(abortController)

      try {
        const result = await uploadFn(chunk, index, context, abortController.signal)
        this.activeAbortControllers.delete(abortController)
        return result
      } catch (error: any) {
        this.activeAbortControllers.delete(abortController)

        // Handle abort (from pause or cancel)
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          if (this.paused) throw new UploadPausedError()
          if (this.canceled) throw new Error('canceled')
        }

        if (this.canceled) throw new Error('canceled')
        if (this.paused) throw new UploadPausedError()

        // Log retry attempt and wait before retrying with exponential backoff
        const retryDelay = calculateRetryDelay(attempt)
        console.log(
          `Chunk ${index} upload failed (attempt ${attempt + 1}), retrying in ${retryDelay / 1000}s...`,
        )
        await this.delay(retryDelay)
        attempt++
      }
    }
  }

  /**
   * Get indices of chunks that failed to upload
   * Strict validation: chunk must have blkWritten[i] === true to pass
   */
  protected getFailedChunkIndices(
    blkWritten: Record<string, boolean>,
    totalChunks: number,
  ): number[] {
    const failed: number[] = []
    for (let i = 0; i < totalChunks; i++) {
      if (blkWritten[i.toString()] !== true) {
        failed.push(i)
      }
    }
    return failed
  }

  /**
   * Merge server blkWritten maps from successful chunk responses.
   * When the server does not return blkWritten for a resolved chunk request,
   * treat that chunk as stored so resumable uploads can continue.
   */
  protected mergeBlkWrittenFromResults<TResponse extends ChunkUploadResponse>(
    results: TaskResult<TResponse>[],
    current: Record<string, boolean> = {},
  ): Record<string, boolean> {
    const blkWritten = { ...current }

    results.forEach((result) => {
      if (!result.success) return

      const responseBlkWritten = result.result?.info?.blkWritten
      if (responseBlkWritten) {
        Object.assign(blkWritten, responseBlkWritten)
      } else {
        blkWritten[result.index.toString()] = true
      }
    })

    return blkWritten
  }

  /**
   * Verify blkWritten status and retry chunks that weren't stored
   * Strict validation: all chunks must have blkWritten === true
   * Retries indefinitely until all chunks pass or upload is paused/canceled
   */
  protected async verifyAndRetryFailedChunks<TContext, TResponse extends ChunkUploadResponse>(
    chunkList: Blob[],
    context: TContext,
    results: TaskResult<TResponse>[],
    pool: ConcurrentPool<TResponse>,
    uploadFn: ChunkUploadFn<TContext, TResponse>,
  ): Promise<void> {
    // Get blkWritten from successful responses
    let blkWritten = this.mergeBlkWrittenFromResults(results)
    if (Object.keys(blkWritten).length === 0) {
      console.warn(
        '[blkWritten verification] No blkWritten info available, all chunks need verification',
      )
    }

    let failedIndices = this.getFailedChunkIndices(blkWritten, chunkList.length)
    if (failedIndices.length === 0) {
      return
    }

    let retry = 0
    while (failedIndices.length > 0) {
      retry++
      console.log(
        `[blkWritten verification] Retrying ${failedIndices.length} failed chunks (attempt ${retry})`,
      )

      if (this.canceled) throw new Error('canceled')
      if (this.paused) throw new UploadPausedError()

      const retryTasks = failedIndices.map((i) => ({
        index: i,
        task: () => this.uploadSingleChunkWithRetry(chunkList[i], i, context, uploadFn),
      }))

      const retryResults = await pool.execute(retryTasks)

      // Check pause/cancel after pool execution
      if (this.canceled) throw new Error('canceled')
      if (this.paused) throw new UploadPausedError()

      // Get updated blkWritten from retry results
      blkWritten = this.mergeBlkWrittenFromResults(retryResults, blkWritten)

      failedIndices = this.getFailedChunkIndices(blkWritten, chunkList.length)

      // If still have failed chunks, wait before next retry with exponential backoff
      if (failedIndices.length > 0) {
        const retryDelay = calculateRetryDelay(retry)
        await this.delay(retryDelay)
      }
    }
  }

  /**
   * Create a ConcurrentPool configured for this uploader
   */
  protected createPool<TResponse>(): ConcurrentPool<TResponse> {
    return new ConcurrentPool<TResponse>({
      concurrency: this.concurrency,
      shouldAbort: () => this.shouldAbort(),
    })
  }

  /**
   * Reset uploader state for a new upload
   */
  protected resetState(): void {
    this.canceled = false
    this.paused = false
    this.activeAbortControllers.clear()
  }
}
